/**
 * @description Merchant Record Payment page for an invoice.
 * Process Card sends paymentMethod `card`. External / Link send UI aliases
 * (cash, check, wire, credit_card, other) that the backend maps onto
 * transactions.chk_payment_method before INSERT.
 *
 * @dependencies invoiceService.recordPayment, PaymentProcessingService
 * @notes Do not send `linked` as a payment method — it violates the
 *        transactions check constraint. Use the source transaction method.
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/AuthContext';
import invoiceService from '../../services/api/invoiceService';
import { transactionsService } from '../../services/api';
import creditMemoService from '../../services/api/creditMemoService';
import {
  formatInvoiceDate,
  formatDateForInput,
  formatCurrency,
  calculateRemainingBalance,
  currencyGreaterThan,
  roundToDecimal
} from '../../utils/invoiceHelpers';
import { getCustomerSearchString } from '../../utils/customerHelpers';
import CardEntry from '../shared/CardEntry';
import StoredCardSelector from '../shared/StoredCardSelector';
import usePaymentStatus from '../../hooks/usePaymentStatus';
import SurchargeConsentModal from '../shared/SurchargeConsentModal';
import PaymentErrorBanner from '../shared/PaymentErrorBanner';
import PaymentTokenizationService from '../../services/PaymentTokenizationService';
import { gatewaysService } from '../../services/api';

function PaymentRecorder() {
  const { user } = useUser();
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();
  const paymentConfigured = usePaymentStatus();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('link'); // 'link', 'card', or 'external'

  // Link transaction state
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Card payment state
  const [cardPayment, setCardPayment] = useState({
    cardNumber: '',
    cardNumberFormatted: '',
    brand: '',
    last4: '',
    cardholderName: '',
    expDate: '',
    cvv: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: ''
  });
  const [processingCard, setProcessingCard] = useState(false);

  // Surcharge two-phase state (matches portal flow)
  const [surchargeInfo, setSurchargeInfo] = useState(null);
  const [surchargeConsentPending, setSurchargeConsentPending] = useState(false);
  const [pendingCardData, setPendingCardData] = useState(null);
  const [hpfSessionKey, setHpfSessionKey] = useState(0); // Increment to force HPF remount

  // Wallet — selected saved card (null = use a new card via HPF)
  const [selectedStoredCard, setSelectedStoredCard] = useState(null);

  // Idempotency key — persists across retries within the same payment session.
  // Generated once per card payment attempt, sent to both gateway + record endpoints.
  // Resets when user starts a new payment session (new HPF mount, navigation, etc.)
  const idempotencyKeyRef = useRef(null);

  // External payment state
  const [externalPayment, setExternalPayment] = useState({
    amount: '',
    paymentDate: formatDateForInput(new Date()),
    paymentMethod: 'cash',
    referenceNumber: '',
    notes: ''
  });

  // Payment allocation
  const [paymentAmount, setPaymentAmount] = useState('');
  const [remainingBalance, setRemainingBalance] = useState(0);

  // Credit memo state
  const [availableCredit, setAvailableCredit] = useState(0);
  const [applyCredit, setApplyCredit] = useState(true);
  const [, setCreditLoading] = useState(false);

  // Fetch invoice details
  useEffect(() => {
    if (!invoiceNumber || invoiceNumber === 'undefined') {
      setError('Invalid invoice number. Please navigate from the invoice list.');
      setLoading(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const data = await invoiceService.getInvoice(invoiceNumber);
        setInvoice(data.invoice || data);

        // Calculate remaining balance with proper rounding
        const invoiceData = data.invoice || data;
        const totalAmount = invoiceData.totalAmount || 0;
        const amountPaid = invoiceData.amountPaid || 0;
        const remaining = calculateRemainingBalance(totalAmount, amountPaid);
        setRemainingBalance(remaining);
        setPaymentAmount(remaining.toFixed(2));

        // Set external payment amount to remaining balance
        setExternalPayment(prev => ({ ...prev, amount: remaining.toFixed(2) }));
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceNumber]);

  // Fetch customer credit when invoice loads
  useEffect(() => {
    const fetchCustomerCredit = async () => {
      if (!invoice?.customerPublicId) return;

      try {
        setCreditLoading(true);
        // Credit endpoint uses resolveCustomer which requires "cust-{publicId}" format
        const customerId = `cust-${invoice.customerPublicId}`;
        const creditData = await creditMemoService.getCustomerCredit(customerId);
        const credit = creditData.availableCreditCents ? creditData.availableCreditCents / 100 : 0;
        setAvailableCredit(credit);

        // Match portal pattern: if credit is available and applyCredit defaults to true,
        // auto-adjust payment amount to reflect amount due after credit
        if (credit > 0 && remainingBalance > 0) {
          const creditAmount = Math.min(credit, remainingBalance);
          const amountDueAfterCredit = Math.max(0, remainingBalance - creditAmount);
          setPaymentAmount(amountDueAfterCredit.toFixed(2));
        }
      } catch (err) {
        console.error('Error fetching customer credit:', err);
        // Don't show error - credit is optional
        setAvailableCredit(0);
      } finally {
        setCreditLoading(false);
      }
    };

    fetchCustomerCredit();
  }, [invoice?.customerPublicId, remainingBalance]);

  // Fetch transactions for linking
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user?.id || activeTab !== 'link') return;

      try {
        const data = await transactionsService.getMerchantTransactions();
        // Filter for approved transactions that aren't already fully allocated to invoices
        // Only unlinked transactions (created via "Create > New Payment" without an invoice) should be linkable
        const availableTransactions = data.filter(tx =>
          tx.status === 'APPROVAL' &&
          !tx.invoiceId &&
          (parseFloat(tx.amount || 0) - parseFloat(tx.allocatedAmount || 0)) > 0
        );
        setTransactions(availableTransactions);
      } catch (err) {
        console.error('Error fetching transactions:', err);
      }
    };

    fetchTransactions();
  }, [user, activeTab]);

  // Filter transactions based on search
  const filteredTransactions = transactions.filter(tx => {
    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();
    const reference = (tx.transactionReference || '').toLowerCase();
    const amount = tx.amount?.toString() || '';
    return reference.includes(search) ||
           amount.includes(search) ||
           getCustomerSearchString(tx).includes(search);
  });

  // Check allocation availability for transactions
  const getTransactionAllocationInfo = (transaction) => {
    const totalAmount = parseFloat(transaction.amount || 0);
    const allocatedAmount = parseFloat(transaction.allocatedAmount || 0);
    const availableAmount = totalAmount - allocatedAmount;
    const isFullyAllocated = availableAmount <= 0;

    return {
      totalAmount,
      allocatedAmount,
      availableAmount,
      isFullyAllocated,
      percentAllocated: totalAmount > 0 ? (allocatedAmount / totalAmount) * 100 : 0
    };
  };

  // Handle transaction selection
  const handleTransactionSelect = (transaction) => {
    const allocationInfo = getTransactionAllocationInfo(transaction);
    if (allocationInfo.isFullyAllocated) {
      setError(['This transaction has been fully allocated to other invoices']);
      return;
    }

    setSelectedTransaction(transaction);
    // Set payment amount to the minimum of available allocation or remaining invoice balance
    const maxPayment = Math.min(allocationInfo.availableAmount, remainingBalance);
    setPaymentAmount(roundToDecimal(maxPayment).toFixed(2));
  };

  // Handle external payment change
  const handleExternalPaymentChange = (field, value) => {
    setExternalPayment(prev => ({ ...prev, [field]: value }));

    if (field === 'amount') {
      setPaymentAmount(value);
    }
  };

  /**
   * Execute the card payment (Phase 2) — called directly or after surcharge consent.
   * Single atomic call: backend charges gateway + records payment in one transaction.
   * If the charge succeeds but recording fails, backend auto-voids the charge.
   *
   * @param {string} shopperInteraction - Elavon EPG interaction type for this charge.
   *   Lynx never uses a physical POS reader, so every charge is card-not-present (CNP).
   *   The caller picks the most accurate CNP enum value per the flow:
   *   - 'telephoneOrder' for new-card merchant Record Payment (MOTO — merchant keyed
   *     the PAN from a customer-provided card)
   *   - 'merchantInitiated' for charging a customer's saved card from the wallet
   *     selector (no shopper actively present)
   *   - 'ecommerce' is the customer-portal path only (handled in PortalPayment.js,
   *     not here)
   *
   *   US-only assumption: `telephoneOrder` is acceptable as a CNP catch-all under
   *   Visa/MC US rules. For PSD2/SCA markets (EU/UK), `telephoneOrder` is NOT
   *   eligible for the same Strong Customer Authentication exemptions as a properly-
   *   classified MIT — revisit this defaulting before any EU rollout.
   *
   *   See `.claude/tasks/SURCHARGE_ADVICE_MIGRATION.md` for the full matrix.
   */
  const executeCardPayment = async (formData, storedCard, surchargeAdviceId, shopperInteraction) => {
    // Generate idempotency key once per payment attempt (persists across retries)
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    const idempotencyKey = idempotencyKeyRef.current;

    // Single call — backend handles gateway charge + payment recording atomically
    await invoiceService.recordPayment(invoiceNumber, {
      amount: parseFloat(paymentAmount),
      paymentDate: new Date().toISOString(),
      paymentMethod: 'card',
      storedCardId: storedCard.id,
      brand: formData.brand,
      last4: formData.last4,
      cardholderName: formData.holderName,
      billingAddress: formData.billingAddress,
      billingCity: formData.billingCity,
      billingState: formData.billingState,
      billingZip: formData.billingZip,
      surchargeAdviceId: surchargeAdviceId || undefined,
      shopperId: storedCard.gatewayShopperId || undefined,
      shopperInteraction,
      // Every merchant Record Payment is by definition a stored-credential transaction:
      // new-card flow vaults during the charge; saved-card flow uses an existing vault.
      // Tag both as 'unscheduled' per Visa SCF (Elavon's enum doesn't have 'initial'/'setup'
      // for the first stored-credential transaction). Backend ElavonGateway defaults this
      // to 'unscheduled' too — being explicit makes the intent legible at the API call site
      // and locks the contract regardless of future backend default changes.
      credentialOnFileType: 'unscheduled',
      notes: `Card payment - ${formData.brand || 'Card'} ending in ${formData.last4 || '****'}`
    }, { idempotencyKey });

    setCardPayment({
      brand: formData.brand,
      last4: formData.last4,
      billingAddress: formData.billingAddress,
      billingCity: formData.billingCity,
      billingState: formData.billingState,
      billingZip: formData.billingZip
    });

    // Payment succeeded — clear idempotency key for next payment session
    idempotencyKeyRef.current = null;
  };

  /** Accept surcharge — resume payment with surchargeAdviceId, then apply credit. */
  const handleSurchargeAccept = async () => {
    setSurchargeConsentPending(false);
    setProcessingCard(true);
    setSaving(true);
    try {
      await executeCardPayment(
        pendingCardData.formData,
        pendingCardData.storedCard,
        surchargeInfo.adviceId,
        pendingCardData.shopperInteraction
      );

      // Apply credit AFTER successful payment — mirrors the no-surcharge path
      // in handleCardFormSubmit. Without this, surcharge+credit payments silently
      // drop the credit memo application.
      const creditToApplyAmount = applyCredit && availableCredit > 0
        ? Math.min(availableCredit, remainingBalance)
        : 0;
      if (creditToApplyAmount > 0) {
        await applyCreditAfterPayment(creditToApplyAmount);
      }

      navigate(`/merchant/invoices/${invoiceNumber}`);
    } catch (err) {
      console.error('Payment failed after surcharge consent:', err);
      setError(err);
    } finally {
      setProcessingCard(false);
      setSaving(false);
    }
  };

  /** Decline surcharge — reset HPF session so merchant can retry with fresh fields */
  const handleSurchargeDecline = () => {
    setSurchargeConsentPending(false);
    setSurchargeInfo(null);
    setPendingCardData(null);
    idempotencyKeyRef.current = null; // New payment session → new key
    // Increment key to force CardEntry/HPF remount with fresh session
    setHpfSessionKey(prev => prev + 1);
  };

  /**
   * Charge a saved (vaulted) card. Skips Phase 1 (vault HPF → HostedCard) since
   * the card is already in the vault — goes straight to surcharge check + execute.
   * Billing fields intentionally omitted: the StoredCard already carries billTo
   * from when it was vaulted, and backend only validates billing when fields are present.
   */
  const handleStoredCardSubmit = async () => {
    if (!selectedStoredCard) return;
    try {
      setProcessingCard(true);
      setError(null);
      setSaving(true);

      const amountInCents = Math.round(parseFloat(paymentAmount) * 100);

      // formData stand-in for executeCardPayment — only brand/last4/holderName are read
      const formData = {
        brand: selectedStoredCard.cardBrand,
        last4: selectedStoredCard.lastFour,
        holderName: selectedStoredCard.cardholderName || ''
      };

      let surcharge = null;
      try {
        const surchargeResult = await gatewaysService.checkSurcharge({
          storedCardId: selectedStoredCard.gatewayCardId,
          amountCents: amountInCents
        });
        surcharge = surchargeResult.surcharge;
      } catch (surchargeErr) {
        console.error('Surcharge check failed:', surchargeErr);
      }

      // Saved-card flow: merchant is charging a card that the customer previously
      // authorized for storage. The shopper isn't actively driving this charge, so
      // it's an MIT ad-hoc charge — tag accordingly for Visa SCF.
      const shopperInteraction = 'merchantInitiated';

      if (surcharge) {
        setSurchargeInfo(surcharge);
        setPendingCardData({ formData, storedCard: selectedStoredCard, shopperInteraction });
        setSurchargeConsentPending(true);
        setProcessingCard(false);
        setSaving(false);
        return;
      }

      await executeCardPayment(formData, selectedStoredCard, null, shopperInteraction);

      const creditToApplyAmount = applyCredit && availableCredit > 0
        ? Math.min(availableCredit, remainingBalance)
        : 0;
      if (creditToApplyAmount > 0) {
        await applyCreditAfterPayment(creditToApplyAmount);
      }

      navigate(`/merchant/invoices/${invoiceNumber}`);
    } catch (err) {
      console.error('Stored card payment error:', err);
      setError(err);
    } finally {
      setProcessingCard(false);
      setSaving(false);
    }
  };

  /**
   * Handle card form submission — gateway-aware.
   * HPF path: card data never touches our code (PCI SAQ-A).
   * Legacy path: tokenize locally then record payment (existing flow).
   */
  const handleCardFormSubmit = async (formData) => {
    try {
      setProcessingCard(true);
      setError(null);
      setSaving(true);

      const amountInCents = Math.round(parseFloat(paymentAmount) * 100);

      if (formData.type === 'hostedCard') {
        // ── HPF Path: Two-phase surcharge flow (matches portal) ──
        // Phase 1: Vault card → check surcharge → show consent if applicable
        // Phase 2: Charge with surchargeAdviceId after consent

        // Step 1: Vault the hosted card
        const vaultResult = await gatewaysService.vaultCard(formData.sessionId, {
          hostedCardId: formData.hostedCardId,
          customerId: invoice?.customerId || null,
          customerData: { fullName: formData.holderName }
        });

        const storedCard = vaultResult.storedCard;

        // Step 2: Check surcharge eligibility
        let surcharge = null;
        try {
          const surchargeResult = await gatewaysService.checkSurcharge({
            storedCardId: storedCard.gatewayCardId,
            amountCents: amountInCents
          });
          surcharge = surchargeResult.surcharge;
        } catch (surchargeErr) {
          // Non-fatal: proceed without surcharge
          console.error('Surcharge check failed:', surchargeErr);
        }

        // New-card merchant Record Payment via HPF. Lynx has no POS terminal, so the merchant
        // key-entered the PAN — by definition card-not-present. Customer typically dictated
        // the card over the phone or in person; either way, MOTO ('telephoneOrder') is the
        // most honest Elavon enum value. Visa/MC treat telephoneOrder and ecommerce as both
        // CNP, and MOTO often gets slightly looser auth rules — net improvement.
        const shopperInteraction = 'telephoneOrder';

        if (surcharge) {
          // Surcharge applies — pause and show consent modal
          setSurchargeInfo(surcharge);
          setPendingCardData({
            formData,
            storedCard,
            shopperInteraction
          });
          setSurchargeConsentPending(true);
          setProcessingCard(false);
          setSaving(false);
          return; // Wait for user consent
        }

        // No surcharge — charge immediately
        await executeCardPayment(formData, storedCard, null, shopperInteraction);

      } else {
        // ── Legacy Path: No gateway configured — tokenize locally ──
        // Intentionally omits shopperInteraction + credentialOnFileType. Every production
        // company has Elavon configured, so this branch is effectively dead. If revived,
        // the caller should think through SCF tagging at that time rather than inheriting
        // a default that may be wrong for whatever new gateway is being used.
        const tokenResult = await PaymentTokenizationService.tokenize({
          cardNumber: formData.cardNumber,
          cvv: formData.cvv,
          expiryMonth: formData.expiryMonth,
          expiryYear: formData.expiryYear,
          cardholderName: formData.cardholderName
        });

        setCardPayment({
          token: tokenResult.token,
          brand: tokenResult.brand,
          last4: tokenResult.last4,
          cardholderName: formData.cardholderName,
          expDate: `${tokenResult.expiryMonth}/${tokenResult.expiryYear}`,
          billingAddress: formData.billingAddress,
          billingCity: formData.billingCity,
          billingState: formData.billingState,
          billingZip: formData.billingZip
        });

        await invoiceService.recordPayment(invoiceNumber, {
          amount: parseFloat(paymentAmount),
          paymentDate: new Date().toISOString(),
          paymentMethod: 'card',
          token: tokenResult.token,
          brand: tokenResult.brand,
          last4: tokenResult.last4,
          cardholderName: formData.cardholderName,
          billingAddress: formData.billingAddress,
          billingCity: formData.billingCity,
          billingState: formData.billingState,
          billingZip: formData.billingZip,
          // AVS/CVV response codes come from the gateway after processing — not set client-side
          referenceNumber: `Merchant-${Date.now()}`,
          notes: `Card payment - ${tokenResult.brand} ending in ${tokenResult.last4} - ${formData.cardholderName}`
        });
      }

      // Apply credit AFTER successful payment
      const creditToApplyAmount = applyCredit && availableCredit > 0
        ? Math.min(availableCredit, remainingBalance)
        : 0;
      if (creditToApplyAmount > 0) {
        await applyCreditAfterPayment(creditToApplyAmount);
      }

      // Navigate back to invoice details on success
      navigate(`/merchant/invoices/${invoiceNumber}`);

    } catch (error) {
      console.error('Payment processing error:', error);
      // Store the raw error — PaymentErrorBanner handles normalization and display
      setError(error);

    } finally {
      setProcessingCard(false);
      setSaving(false);
    }
  };

  // Validate payment amount
  const validatePayment = () => {
    const amount = parseFloat(paymentAmount);
    const errorMessages = [];

    // Allow $0 payment when credit fully covers the balance (matches portal credit-only flow)
    const creditCoversAll = applyCredit && availableCredit > 0 &&
      Math.min(availableCredit, remainingBalance) >= remainingBalance;

    if (!creditCoversAll && (isNaN(amount) || amount <= 0)) {
      errorMessages.push('Enter a valid payment amount greater than $0.00');
    }

    // Use safe comparison for floating point numbers
    if (currencyGreaterThan(amount, remainingBalance)) {
      errorMessages.push(`Payment amount cannot exceed remaining balance of ${formatCurrency(remainingBalance)}`);
    }

    if (activeTab === 'link' && !selectedTransaction && !creditCoversAll) {
      errorMessages.push('Select a transaction to link to this invoice');
    }

    if (activeTab === 'card' && !creditCoversAll) {
      if (!cardPayment.cardNumber || cardPayment.cardNumber.length < 13) {
        errorMessages.push('Enter a valid card number');
      }
      if (!cardPayment.expDate || !cardPayment.expDate.match(/^\d{2}\/\d{2}$/)) {
        errorMessages.push('Enter expiration date in MM/YY format');
      }
      if (!cardPayment.cvv || cardPayment.cvv.length < 3) {
        errorMessages.push('Enter a valid CVV');
      }
      if (!cardPayment.billingAddress) {
        errorMessages.push('Enter billing address');
      }
      if (!cardPayment.billingZip) {
        errorMessages.push('Enter billing ZIP code');
      }
    }

    if (errorMessages.length > 0) {
      setError(errorMessages);
      return false;
    }

    return true;
  };

  // Helper function to apply credit after successful payment
  const applyCreditAfterPayment = async (creditToApplyAmount) => {
    if (creditToApplyAmount <= 0) return;

    try {
      if (!invoice.customerPublicId) return;
      const customerId = `cust-${invoice.customerPublicId}`;

      // Get available credit memos and apply them to this invoice
      const creditMemos = await creditMemoService.getCustomerCreditMemos(customerId);
      let remainingCreditToApply = creditToApplyAmount;

      for (const memo of (creditMemos.creditMemos || [])) {
        if (remainingCreditToApply <= 0) break;
        if (memo.status !== 'active' && memo.status !== 'partially_applied') continue;

        const memoRemainingBalance = (memo.remainingBalanceCents || 0) / 100;
        if (memoRemainingBalance <= 0) continue;

        const amountFromThisMemo = Math.min(remainingCreditToApply, memoRemainingBalance);
        const amountCents = Math.round(amountFromThisMemo * 100);

        await creditMemoService.applyCreditToInvoice(memo.id, invoice.id, amountCents);
        remainingCreditToApply -= amountFromThisMemo;

        console.log(`Applied ${formatCurrency(amountFromThisMemo)} credit from memo ${memo.creditMemoNumber}`);
      }
    } catch (creditErr) {
      // Log but don't fail - payment already succeeded
      console.error('Error applying credit after payment:', creditErr);
    }
  };

  // Handle payment submission
  const handleSubmit = async () => {
    if (!validatePayment()) return;

    try {
      setSaving(true);
      setError(null);

      // Calculate credit to apply (will be applied AFTER successful payment)
      const creditToApplyAmount = applyCredit && availableCredit > 0
        ? Math.min(availableCredit, remainingBalance)
        : 0;

      // Credit-only flow: credit fully covers the balance (matches portal pattern)
      const paymentAmountValue = parseFloat(paymentAmount) || 0;
      if (creditToApplyAmount > 0 && paymentAmountValue <= 0) {
        await applyCreditAfterPayment(creditToApplyAmount);
        navigate(`/merchant/invoices/${invoiceNumber}`);
        return;
      }

      if (activeTab === 'card') {
        // Process card payment first
        setProcessingCard(true);

        // Create transaction through the transactions API
        const transactionData = {
          userId: user.id,
          amount: paymentAmount,
          cardType: cardPayment.cardType || 'visa',
          cardNumber: cardPayment.cardNumber,
          cardholderName: cardPayment.cardholderName,
          // AVS/CVV response codes come from the gateway after processing — not set client-side
          customerId: invoice.customerPublicId ? `cust-${invoice.customerPublicId}` : null,
          avsAddress: cardPayment.billingAddress,
          avsCity: cardPayment.billingCity,
          avsState: cardPayment.billingState,
          avsZip: cardPayment.billingZip
        };

        const transactionResponse = await transactionsService.createMockTransaction(transactionData);

        // Record the payment with the transaction
        const paymentData = {
          amount: parseFloat(paymentAmount),
          paymentDate: new Date().toISOString(),
          paymentMethod: 'card',
          referenceNumber: transactionResponse.transaction_reference || transactionResponse.transactionId,
          notes: `Card payment - ${cardPayment.cardType?.toUpperCase() || 'CARD'} ending in ${cardPayment.cardNumber.slice(-4)}`,
          transactionId: transactionResponse.id
        };

        await invoiceService.recordPayment(invoiceNumber, paymentData);

        // Apply credit AFTER successful payment (CRITICAL FIX)
        if (creditToApplyAmount > 0) {
          await applyCreditAfterPayment(creditToApplyAmount);
        }

        // Navigate back to invoice details
        navigate(`/merchant/invoices/${invoiceNumber}`);
      } else {
        // Existing logic for link and external payments
        const paymentData = {
          amount: parseFloat(paymentAmount),
          paymentDate: activeTab === 'link'
            ? new Date().toISOString()
            : externalPayment.paymentDate,
          paymentMethod: activeTab === 'link'
            ? (selectedTransaction.paymentMethod || selectedTransaction.payment_method || 'external')
            : externalPayment.paymentMethod,
          referenceNumber: activeTab === 'link'
            ? selectedTransaction.transactionReference
            : externalPayment.referenceNumber,
          notes: activeTab === 'link'
            ? `Linked to transaction ${selectedTransaction.transactionReference}`
            : externalPayment.notes,
          transactionId: activeTab === 'link' ? selectedTransaction.id : null
        };

        await invoiceService.recordPayment(invoiceNumber, paymentData);

        // Apply credit AFTER successful payment (CRITICAL FIX)
        if (creditToApplyAmount > 0) {
          await applyCreditAfterPayment(creditToApplyAmount);
        }

        // Navigate back to invoice details
        navigate(`/merchant/invoices/${invoiceNumber}`);
      }
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(['Failed to record payment', 'Please try again or contact support']);
      setProcessingCard(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand border-t-transparent"></div>
          <p className="text-sm text-gray-500">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Invoice Not Found</h3>
                <p className="mt-1 text-sm text-red-700">The invoice you're looking for doesn't exist or has been deleted.</p>
                <button
                  onClick={() => navigate('/merchant/invoices')}
                  className="mt-4 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Invoices
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const paymentStatus = invoice.paymentStatus;
  const isPaid = paymentStatus === 'paid';

  return (
    <div className="min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-[72px] flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/merchant/invoices/${invoiceNumber}`)}
                className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Record Payment</h1>
                <p className="text-sm text-gray-500">Invoice {invoice.invoiceNumber}</p>
              </div>
            </div>
            {!isPaid && (() => {
              const creditCoversAll = applyCredit && availableCredit > 0 &&
                Math.min(availableCredit, remainingBalance) >= remainingBalance;
              const showButton = activeTab !== 'card' || creditCoversAll;
              if (!showButton) return null;
              return (
                <button
                  onClick={handleSubmit}
                  disabled={saving || (!creditCoversAll && activeTab === 'link' && !selectedTransaction) || processingCard}
                  className={`h-11 px-6 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 ${
                    creditCoversAll
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-brand text-white hover:bg-brand-hover'
                  }`}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {creditCoversAll ? 'Applying Credit...' : 'Recording...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {creditCoversAll ? 'Apply Credit & Complete' : 'Record Payment'}
                    </>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        <PaymentErrorBanner
          error={error}
          onDismiss={() => setError(null)}
          className="mb-6"
        />

        {/* Paid Alert */}
        {isPaid && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-green-800">Invoice Fully Paid</h3>
                <p className="mt-1 text-sm text-green-700">This invoice has already been paid in full. No additional payments are needed.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Summary Card */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Invoice Summary</h2>
                <p className="text-sm text-gray-500 mt-0.5">Payment details for this invoice</p>
              </div>
              <div className="px-6 py-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {invoice.company ||
                       `${invoice.firstName || ''} ${invoice.lastName || ''}`.trim() ||
                       'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Invoice Total</p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(invoice.totalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Amount Paid</p>
                    <p className="text-sm font-semibold text-green-600">{formatCurrency(invoice.amountPaid || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Remaining</p>
                    <p className="text-sm font-semibold text-red-600">{formatCurrency(remainingBalance)}</p>
                  </div>
                </div>
              </div>
            </div>

            {!isPaid && (
              <>
                {/* Payment Method Tabs */}
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h2 className="text-base font-semibold text-gray-900">Payment Method</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Choose how you'd like to record this payment</p>
                  </div>

                  {/* Tab Navigation */}
                  <div className="px-6 pt-4">
                    <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                      <button
                        onClick={() => setActiveTab('link')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'link'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span className="hidden sm:inline">Link Transaction</span>
                        <span className="sm:hidden">Link</span>
                      </button>
                      <button
                        onClick={() => setActiveTab('card')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'card'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <span className="hidden sm:inline">Process Card</span>
                        <span className="sm:hidden">Card</span>
                      </button>
                      <button
                        onClick={() => setActiveTab('external')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'external'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="hidden sm:inline">External</span>
                        <span className="sm:hidden">Other</span>
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-6">
                    {/* Link Transaction Tab */}
                    {activeTab === 'link' && (
                      <div className="space-y-4">
                        {/* Search */}
                        <div className="relative">
                          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full h-11 pl-12 pr-4 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand focus:border-brand transition-shadow"
                            placeholder="Search by reference, amount, or customer..."
                          />
                        </div>

                        {/* Transaction List */}
                        {filteredTransactions.length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-xl">
                            <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </div>
                            <p className="text-sm font-medium text-gray-900">No unlinked transactions</p>
                            <p className="mt-1 text-sm text-gray-500">All approved transactions may already be linked</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                            {filteredTransactions.map(tx => {
                              const allocationInfo = getTransactionAllocationInfo(tx);
                              const isSelected = selectedTransaction?.id === tx.id;
                              return (
                                <div
                                  key={tx.id}
                                  onClick={() => !allocationInfo.isFullyAllocated && handleTransactionSelect(tx)}
                                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                                    allocationInfo.isFullyAllocated
                                      ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                      : isSelected
                                        ? 'border-brand bg-brand-light cursor-pointer'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                                  }`}
                                >
                                  <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="font-semibold text-gray-900 truncate">{tx.transactionReference}</p>
                                        {isSelected && (
                                          <div className="w-5 h-5 rounded-full bg-brand flex items-center justify-center flex-shrink-0">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-600 mt-1 truncate">
                                        {tx.firstName} {tx.lastName}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {formatInvoiceDate(tx.date || tx.createdAt)}
                                      </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <p className="font-semibold text-gray-900">{formatCurrency(tx.amount)}</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {tx.cardType || 'Bank Transfer'}
                                      </p>
                                      {allocationInfo.allocatedAmount > 0 && (
                                        <div className="mt-2">
                                          <div className="text-xs text-gray-600">
                                            {formatCurrency(allocationInfo.allocatedAmount)} allocated
                                          </div>
                                          <div className="w-20 bg-gray-200 rounded-full h-1.5 mt-1">
                                            <div
                                              className={`h-1.5 rounded-full ${
                                                allocationInfo.isFullyAllocated ? 'bg-gray-400' : 'bg-brand'
                                              }`}
                                              style={{ width: `${Math.min(allocationInfo.percentAllocated, 100)}%` }}
                                            />
                                          </div>
                                          {!allocationInfo.isFullyAllocated && (
                                            <div className="text-xs text-green-600 mt-1">
                                              {formatCurrency(allocationInfo.availableAmount)} available
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Process Card Tab — always mounted so HPF iFrames persist across tab switches */}
                    {(() => {
                      const creditCoversAll = applyCredit && availableCredit > 0 &&
                        Math.min(availableCredit, remainingBalance) >= remainingBalance;
                      const isCardTab = activeTab === 'card';

                      return (
                        <div style={{ display: isCardTab ? 'block' : 'none' }}>
                          {creditCoversAll ? (
                            <div className="text-center py-8">
                              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <p className="text-gray-700 mb-2">
                                Customer credit of <span className="font-semibold text-green-600">{formatCurrency(availableCredit)}</span> fully covers this invoice.
                              </p>
                              <p className="text-sm text-gray-500">
                                No card payment required. Use the button above to apply credit.
                              </p>
                            </div>
                          ) : paymentConfigured === false ? (
                            <div className="text-center py-12">
                              <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                              </div>
                              <p className="text-sm font-medium text-gray-700">Payment processing is not configured.</p>
                              <p className="text-xs text-gray-500 mt-1">Please contact your administrator to setup payments.</p>
                            </div>
                          ) : (
                            <div className="space-y-6">
                              {/* Wallet — saved cards. Component returns null if customer has no saved cards.
                                  showActions={false}: hide Set Default / Remove inside the payment flow
                                  to prevent accidental wallet management. Card management lives on
                                  the Customer Details page. */}
                              {invoice?.customerId && (
                                <StoredCardSelector
                                  customerId={invoice.customerId}
                                  selectedCardId={selectedStoredCard?.id || null}
                                  onSelect={(card) => setSelectedStoredCard(card)}
                                  onNewCard={() => {
                                    setSelectedStoredCard(null);
                                    // Force fresh HPF session when switching back to new-card entry
                                    setHpfSessionKey(prev => prev + 1);
                                  }}
                                  disabled={processingCard || saving}
                                  label="Wallet"
                                  showActions={false}
                                />
                              )}

                              {selectedStoredCard ? (
                                <button
                                  type="button"
                                  onClick={handleStoredCardSubmit}
                                  disabled={processingCard || saving || !paymentAmount || parseFloat(paymentAmount) <= 0}
                                  className="w-full py-3 px-4 rounded-md font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                                >
                                  {processingCard
                                    ? 'Processing...'
                                    : `Charge ${formatCurrency(parseFloat(paymentAmount) || 0)} to ${selectedStoredCard.cardBrand || 'Card'} ****${selectedStoredCard.lastFour}`}
                                </button>
                              ) : (
                                <CardEntry
                                  key={hpfSessionKey}
                                  onSubmit={handleCardFormSubmit}
                                  processing={processingCard}
                                  error={null}
                                  submitButtonText={`Process Payment ${formatCurrency(parseFloat(paymentAmount) || 0)}`}
                                  amount={Math.round((parseFloat(paymentAmount) || 0) * 100)}
                                  currency="USD"
                                  description={`Invoice ${invoiceNumber} payment`}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* External Payment Tab */}
                    {activeTab === 'external' && (
                      <div className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Payment Date
                            </label>
                            <input
                              type="date"
                              value={externalPayment.paymentDate}
                              onChange={(e) => handleExternalPaymentChange('paymentDate', e.target.value)}
                              className="w-full h-11 px-4 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand focus:border-brand transition-shadow"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Payment Method
                            </label>
                            <select
                              value={externalPayment.paymentMethod}
                              onChange={(e) => handleExternalPaymentChange('paymentMethod', e.target.value)}
                              className="w-full h-11 px-4 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand focus:border-brand transition-shadow bg-white"
                            >
                              <option value="cash">Cash</option>
                              <option value="check">Check</option>
                              <option value="wire">Bank Transfer</option>
                              <option value="credit_card">Credit Card (already collected)</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference Number
                            <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                          </label>
                          <input
                            type="text"
                            value={externalPayment.referenceNumber}
                            onChange={(e) => handleExternalPaymentChange('referenceNumber', e.target.value)}
                            className="w-full h-11 px-4 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand focus:border-brand transition-shadow"
                            placeholder="Check number, transaction ID, etc."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notes
                            <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                          </label>
                          <textarea
                            value={externalPayment.notes}
                            onChange={(e) => handleExternalPaymentChange('notes', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand focus:border-brand transition-shadow resize-none"
                            rows="3"
                            placeholder="Additional payment details..."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-[96px]">
              {/* Payment Allocation Card */}
              {!isPaid && (
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h2 className="text-base font-semibold text-gray-900">Payment Summary</h2>
                  </div>
                  <div className="p-6 space-y-4">
                    {/* Payment Amount Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={remainingBalance}
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="w-full h-11 pl-8 pr-4 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand focus:border-brand transition-shadow"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    {/* Summary Lines */}
                    <div className="pt-4 border-t border-gray-100 space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Invoice Total</span>
                        <span className="font-medium text-gray-900">{formatCurrency(parseFloat(invoice.totalAmount) || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Already Paid</span>
                        <span className="font-medium text-green-600">{formatCurrency(invoice.amountPaid || 0)}</span>
                      </div>

                      {/* Credit Option - Show if customer has available credit (matches portal pattern) */}
                      {availableCredit > 0 && (
                        <div className="flex justify-between items-center text-sm py-2 border-t border-gray-100">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={applyCredit}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setApplyCredit(checked);
                                // Match portal pattern: adjust payment amount when credit toggled
                                const creditAmount = Math.min(availableCredit, remainingBalance);
                                const amountDueAfterCredit = Math.max(0, remainingBalance - creditAmount);
                                setPaymentAmount(checked ? amountDueAfterCredit.toFixed(2) : remainingBalance.toFixed(2));
                              }}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                            />
                            <span className={applyCredit ? 'text-green-600' : 'text-gray-600'}>
                              {applyCredit ? 'Credit Applied' : 'Apply Credit'}
                            </span>
                          </label>
                          <span className={`font-medium ${applyCredit ? 'text-green-600' : 'text-gray-500'}`}>
                            {applyCredit ? '-' : ''}{formatCurrency(Math.min(availableCredit, remainingBalance))}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">
                          {applyCredit && availableCredit > 0 ? 'Amount to Pay' : 'This Payment'}
                        </span>
                        <span className="font-medium text-gray-900">-{formatCurrency(parseFloat(paymentAmount) || 0)}</span>
                      </div>
                    </div>

                    {/* New Balance */}
                    {(() => {
                      const creditAppliedAmount = applyCredit && availableCredit > 0
                        ? Math.min(availableCredit, remainingBalance)
                        : 0;
                      const newBalance = roundToDecimal(
                        remainingBalance - creditAppliedAmount - (parseFloat(paymentAmount) || 0)
                      );
                      return (
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-900">New Balance</span>
                            <span className={`text-lg font-semibold ${
                              newBalance <= 0.001 ? 'text-green-600' : 'text-gray-900'
                            }`}>
                              {formatCurrency(Math.max(0, newBalance))}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Fully Paid Notice */}
                    {(() => {
                      const creditAppliedAmount = applyCredit && availableCredit > 0
                        ? Math.min(availableCredit, remainingBalance)
                        : 0;
                      const newBalance = roundToDecimal(
                        remainingBalance - creditAppliedAmount - (parseFloat(paymentAmount) || 0)
                      );
                      return newBalance <= 0.001;
                    })() && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="text-xs font-medium text-green-800">This will mark invoice as fully paid</p>
                        </div>
                      </div>
                    )}

                    {/* Mobile Submit Button */}
                    {(() => {
                      const creditCoversAll = applyCredit && availableCredit > 0 &&
                        Math.min(availableCredit, remainingBalance) >= remainingBalance;
                      const showButton = activeTab !== 'card' || creditCoversAll;
                      if (!showButton) return null;
                      return (
                        <div className="pt-4 lg:hidden">
                          <button
                            onClick={handleSubmit}
                            disabled={saving || (!creditCoversAll && activeTab === 'link' && !selectedTransaction) || processingCard}
                            className={`w-full h-11 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 ${
                              creditCoversAll
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-brand text-white hover:bg-brand-hover'
                            }`}
                          >
                            {saving ? (
                              <>
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {creditCoversAll ? 'Applying Credit...' : 'Recording...'}
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                {creditCoversAll ? 'Apply Credit & Complete' : 'Record Payment'}
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="mt-6 bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="text-base font-semibold text-gray-900">Quick Actions</h2>
                </div>
                <div className="p-4 space-y-2">
                  <button
                    onClick={() => navigate(`/merchant/invoices/${invoiceNumber}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Invoice
                  </button>
                  <button
                    onClick={() => navigate('/merchant/invoices')}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    All Invoices
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SurchargeConsentModal
        show={surchargeConsentPending && !!surchargeInfo}
        surchargeInfo={surchargeInfo}
        onAccept={handleSurchargeAccept}
        onDecline={handleSurchargeDecline}
        formatCurrency={formatCurrency}
      />
    </div>
  );
}

export default PaymentRecorder;
